import { BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";

interface StatsChartPreviewProps {
  settings: {
    chartType?: string;
    title?: string;
    subtitle?: string;
    showTitle?: boolean;
    showLegend?: boolean;
    showGrid?: boolean;
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    height?: number;
    data?: Array<any>;
    dataKey1?: string;
    dataKey2?: string;
    xAxisKey?: string;
  };
}

export const StatsChartPreview = ({ settings }: StatsChartPreviewProps) => {
  const defaultData = [
    { name: "السبت", مبيعات: 45, مشاهدات: 120 },
    { name: "الأحد", مبيعات: 52, مشاهدات: 145 },
    { name: "الاثنين", مبيعات: 38, مشاهدات: 98 },
    { name: "الثلاثاء", مبيعات: 67, مشاهدات: 178 },
    { name: "الأربعاء", مبيعات: 81, مشاهدات: 210 },
    { name: "الخميس", مبيعات: 73, مشاهدات: 189 },
    { name: "الجمعة", مبيعات: 95, مشاهدات: 245 },
  ];

  const pieData = [
    { name: "القاهرة", value: 400 },
    { name: "الإسكندرية", value: 300 },
    { name: "الجيزة", value: 200 },
    { name: "أخرى", value: 100 },
  ];

  const data = settings.data || defaultData;
  const chartType = settings.chartType || "bar";
  const primaryColor = settings.primaryColor || "#10b981";
  const secondaryColor = settings.secondaryColor || "#3b82f6";
  const height = settings.height || 300;

  const renderChart = () => {
    switch (chartType) {
      case "line":
        return (
          <LineChart data={data}>
            {settings.showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey={settings.xAxisKey || "name"} />
            <YAxis />
            <Tooltip />
            {settings.showLegend && <Legend />}
            <Line type="monotone" dataKey={settings.dataKey1 || "مبيعات"} stroke={primaryColor} strokeWidth={2} />
            {settings.dataKey2 && (
              <Line type="monotone" dataKey={settings.dataKey2} stroke={secondaryColor} strokeWidth={2} />
            )}
          </LineChart>
        );

      case "area":
        return (
          <AreaChart data={data}>
            {settings.showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey={settings.xAxisKey || "name"} />
            <YAxis />
            <Tooltip />
            {settings.showLegend && <Legend />}
            <Area type="monotone" dataKey={settings.dataKey1 || "مبيعات"} fill={primaryColor} stroke={primaryColor} />
            {settings.dataKey2 && (
              <Area type="monotone" dataKey={settings.dataKey2} fill={secondaryColor} stroke={secondaryColor} />
            )}
          </AreaChart>
        );

      case "pie":
        return (
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
              outerRadius={80}
              fill={primaryColor}
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={index % 2 === 0 ? primaryColor : secondaryColor} />
              ))}
            </Pie>
            <Tooltip />
            {settings.showLegend && <Legend />}
          </PieChart>
        );

      default: // bar
        return (
          <BarChart data={data}>
            {settings.showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey={settings.xAxisKey || "name"} />
            <YAxis />
            <Tooltip />
            {settings.showLegend && <Legend />}
            <Bar dataKey={settings.dataKey1 || "مبيعات"} fill={primaryColor} />
            {settings.dataKey2 && <Bar dataKey={settings.dataKey2} fill={secondaryColor} />}
          </BarChart>
        );
    }
  };

  return (
    <div
      className="p-6 rounded-lg"
      style={{ backgroundColor: settings.backgroundColor || "transparent" }}
    >
      {settings.showTitle && (
        <div className="mb-4">
          <h3 className="text-xl font-bold">{settings.title || "إحصائيات المبيعات"}</h3>
          {settings.subtitle && (
            <p className="text-sm opacity-75 mt-1">{settings.subtitle}</p>
          )}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};
